import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, MessageCircle, FileText, Users, ArrowLeft, Phone, Mail, Target, Zap } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const SalesInterviewProcessSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {

  const interviewStages = [
    {
      Icon: MessageCircle,
      title: '1. Intro Interview (30–45 mins)',
      description: 'Usually with a founder, sales lead or hiring manager. Covers your background, sales mindset and motivations.',
      keyPoints: [
        '"Why sales, and why this company?"',
        '"Talk me through a target you hit (or missed)"',
        '"How do you handle rejection?"',
      ],
      pointsTitle: 'Common questions:'
    },
    {
      Icon: Phone,
      title: '2. Cold Call Roleplay and/or Prospecting Case Study',
      description: 'Tests your practical sales skills through simulation and strategic thinking.',
      keyPoints: [
        'Roleplay: simulates a discovery or cold call - you might pitch a product, handle objections or ask discovery questions',
        'Case study: often involves writing cold email and LinkedIn copy, sometimes includes prospect list building or research',
        'You\'ll usually present this live or walk through your work on a call'
      ],
    },
    {
      Icon: Users,
      title: '3. Final Round Interview',
      description: 'Can include culture chats, team interviews or a second round of sales tasks. Often focused on values, grit and ability to take feedback.',
      keyPoints: [
        'May include objection handling or mock second calls',
      ],
    },
  ];

  const whatStartupsCareAbout = [
    {
      icon: '🔥',
      title: 'Hunger',
      description: 'Are you ready to go again after 100s of rejections? Do you revel in the difficulty of it? And do you have a clear WHY to complement this?'
    },
    {
      icon: '🎯',
      title: 'Target-driven',
      description: 'Do you have evidence of exceeding targets?'
    },
    {
      icon: '⚡',
      title: 'Energy',
      description: 'Will you bring good momentum to a small team?'
    },
    {
      icon: '💪',
      title: 'Resilience',
      description: 'Can you keep it up when the going gets tough?'
    },
    {
      icon: '🧠',
      title: 'Coachability',
      description: 'Can you take feedback and apply it fast?'
    }
  ];

  const etiquetteTips = [
    'Reply to startups within 24 hours to keep momentum - this shows reliability',
    'Follow up if a startup hasn\'t replied in 48-72 hours with polite chaser emails',
    'Always send a thank-you email after interviews - reiterate your interest, reference something from the chat and confirm next steps',
    'Reschedule respectfully - if needed, give at least 4 hours\' notice',
    'Treat it like a sales process - the interview is a chance to showcase your sales skills',
    'Every touchpoint is part of your "outreach": how quickly do you reply? Are you concise?',
    'The interviewers are prospects - are you asking good questions and listening?',
    'Your goal is to close - are you moving things forward and showing commitment?'
  ];

  const commonQuestions = [
    'Why sales?',
    'Why startups?',
    'Why do you want to spend your days being rejected 99% of the time?',
    'Why this company and role?',
    'What\'s your approach to cold outreach?',
    'Walk me through a deal you closed (or tried to)',
    'How do you handle a difficult objection?',
    'What\'s your biggest mistake or miss in sales so far?',
    'How do you prioritise who to reach out to and when?'
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              Sales interviews at startups: the essentials
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Whilst every company is a bit different, here's the common flow.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="How startup interviews typically work">
            <div className="space-y-10">
              {interviewStages.map((stage, index) => (
                <div key={index} className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      {index === 0 && "📞 "}{stage.title}
                    </h4>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      {stage.description}
                    </p>
                  </div>
                  {stage.keyPoints && (
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 ml-6">
                      {stage.pointsTitle && <h5 className="font-semibold text-gray-900 mb-4 text-lg">{stage.pointsTitle}</h5>}
                      <div className="space-y-3">
                        {stage.keyPoints.map((point, i) => (
                          <div key={i} className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                            <span className="text-gray-700 leading-relaxed">{point}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </StepCard>

          <StepCard title="What early-stage startups really care about">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                These qualities are often more important than years of experience:
              </p>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {whatStartupsCareAbout.map((quality, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl">{quality.icon}</span>
                      <h5 className="font-bold text-gray-900 text-lg">{quality.title}</h5>
                    </div>
                    <p className="text-gray-600 leading-relaxed">{quality.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="General Interview Etiquette & Tips">
            <div className="space-y-6">
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Professional communication and follow-up shows reliability and genuine interest:
              </p>
              <div className="grid gap-4">
                {etiquetteTips.map((tip, i) => (
                  <div key={i} className="flex items-start space-x-4 bg-white rounded-xl p-4 border border-blue-200">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-800 leading-relaxed">{tip}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mt-8">
                <h5 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-yellow-600" />
                  Key Sales Insight
                </h5>
                <p className="text-gray-700 leading-relaxed">
                  <span className="font-semibold">"If they can't sell themselves, how will they sell the product?"</span> — Startup founder. 
                  Every interaction is an opportunity to demonstrate your sales skills in action.
                </p>
              </div>
            </div>
          </StepCard>

          <StepCard title="Most Common Sales Interview Questions">
            <div className="space-y-6">
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Prepare thoughtful, evidence-backed answers for these frequently asked questions:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {commonQuestions.map((question, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 hover:bg-gray-50 transition-colors">
                    <span className="text-gray-800 font-medium">"{question}"</span>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="What to do if you're not getting traction">
            <div className="space-y-6">
              <p className="text-gray-600 text-lg leading-relaxed">
                If you've applied to lots of roles but have had very few or no interviews, email{' '}
                <a href="mailto:info@jumpstart-uk.com" className="text-[#02B491] hover:text-emerald-600 font-medium">
                  info@jumpstart-uk.com
                </a>
                . We can:
              </p>
              <div className="grid gap-4">
                {[
                  'Help review your profile and applications',
                  'Suggest small changes that could improve traction',
                  'Advise whether to change tack re: the roles you\'re applying for'
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                    <span className="text-gray-700 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-6">
                <p className="text-blue-800 text-sm">
                  <span className="font-semibold">Note:</span> Only reach out if you're genuinely struggling to land interviews because capacity is limited!
                </p>
              </div>
            </div>
          </StepCard>
        </div>

        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white text-[#02B491] rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border-2 border-[#02B491] hover:shadow-emerald-500/20"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesInterviewProcessSection; 